import { memo, useMemo } from "react";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";
import { Route, useHistory, useLocation } from "react-router-dom";
import { BreadcrumbsItem } from "react-breadcrumbs-dynamic";
import JarjestajaBasicInfo from "./JarjestajaBasicInfo";
import JulkisetTiedot from "./JulkisetTiedot";
import common from "@/i18n/definitions/common";
import * as R from "ramda";
import Basedata from "@/basedata";
import { Helmet } from "react-helmet";
import { Tab, Tabs, Typography } from "@mui/material";
import { withStyles } from "@mui/styles";
import { koulutustyypitMap } from "@/utils/constants";
import { localizeRouteKey } from "@/utils/common";
import equal from "react-fast-compare";
import { AppRoute } from "@/routes/index";
import { LocalizedSwitch } from "@/modules/i18n/index";
import kebabCase from "@/i18n/definitions/kebabCase";

const OivaTab = withStyles(theme => ({
  root: {
    minWidth: 0,
    textTransform: "none",
    color: "#333 !important",
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(15),
    padding: 0,
    marginRight: "2rem",
    marginLeft: "0.3em",
    marginTop: "0.3em",
    "&:focus": {
      outline: "0.2rem solid #d1d1d1"
    }
  }
}))(props => <Tab {...props} />);

const OivaTabs = withStyles(() => ({
  root: {},
  indicator: {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "transparent",
    height: "0.3rem !important",
    "& > div": {
      width: "100%",
      backgroundColor: "#4C7A61"
    }
  }
}))(props => <Tabs {...props} TabIndicatorProps={{ children: <div /> }} />);

const Jarjestaja = memo(
  ({
    JarjestamislupaJSX,
    kohteet,
    koulutusmuoto,
    lupa = {},
    user,
    tulevatLuvat = [],
    voimassaOlevaLupa = {}
  }) => {
    const history = useHistory();
    const intl = useIntl();
    const location = useLocation();
    const tabKey = R.last(R.split("/", location.pathname));

    const jarjestaja =
      lupa && lupa.jarjestaja
        ? {
            ...lupa.jarjestaja,
            nimi:
              R.prop(intl.locale, lupa.jarjestaja.nimi) ||
              R.head(R.values(lupa.jarjestaja.nimi))
          }
        : {};

    const breadcrumb = localizeRouteKey(
      intl.locale,
      AppRoute.Jarjestamislupa,
      intl,
      {
        id: jarjestaja.oid,
        koulutusmuoto: koulutusmuoto.kebabCase
      }
    );

    const tabNavRoutes = useMemo(() => {
      // Basic routes (no authentication needed)
      const basicRoutes = [
        {
          path: intl.formatMessage(kebabCase.jarjestamislupa),
          text: intl.formatMessage(
            koulutusmuoto.koulutustyyppi === koulutustyypitMap.VAPAASIVISTYSTYO
              ? common.yllapitamisLupaTitle
              : common.lupaTitle
          ),
          authenticated: true
        },
        {
          path: intl.formatMessage(kebabCase.paatokset),
          exact: true,
          text: intl.formatMessage(common.lupaPaatokset),
          authenticated: true
        }
      ];
      // If user is logged in we are going to show her/him these additional routes.
      // Oiva-daily August 6, 2026: This is most likely dead code left over from
      // removing a feature where education organizers could manage their permits.
      const additionalRoutes =
        user && R.equals(user.oid, R.prop("oid", lupa.jarjestaja))
          ? [
              {
                id: "jarjestamislupa-asia",
                path: intl.formatMessage(kebabCase.jarjestamislupaasiat),
                text: intl.formatMessage(common.asiatTitle),
                authenticated: !!user
              }
            ]
          : [];
      return R.flatten(R.insert(1, basicRoutes, additionalRoutes));
    }, [koulutusmuoto.koulutustyyppi, lupa.jarjestaja, user, intl]);

    return (
      <article className="flex flex-1 flex-col">
        <Helmet htmlAttributes={{ lang: intl.locale }}>
          <title>
            {jarjestaja.nimi}, {koulutusmuoto.paasivunOtsikko} - Oiva
          </title>
        </Helmet>
        <BreadcrumbsItem to={breadcrumb}>{jarjestaja.nimi}</BreadcrumbsItem>
        <div className="sm:w-4/5 mx-auto max-w-8xl px-8 sm:px-0">
          <section className="my-8">
            <Typography component="h1" variant="h1">
              {jarjestaja.nimi}
            </Typography>

            <JarjestajaBasicInfo jarjestaja={jarjestaja} />
          </section>

          <OivaTabs
            value={tabKey}
            indicatorColor="primary"
            textColor="primary"
            onChange={(e, val) => {
              history.push(val);
            }}>
            {tabNavRoutes
              ? R.addIndex(R.map)((route, index) => {
                  return (
                    <OivaTab
                      key={`tab-${index}`}
                      label={route.text}
                      aria-label={route.text}
                      to={route.path}
                      value={route.path}
                    />
                  );
                }, tabNavRoutes)
              : null}
          </OivaTabs>
        </div>
        <div className="flex-1 bg-gray-100 border-t border-solid border-gray-300">
          <LocalizedSwitch>
            <Route
              path={AppRoute.Jarjestamislupa}
              render={() => (
                <div className="border my-8 p-8 sm:p-12 bg-white mx-8 sm:w-4/5 sm:mx-auto max-w-8xl">
                  {JarjestamislupaJSX ? (
                    <Basedata
                      keys={["muut"]}
                      locale={intl.locale}
                      render={props => {
                        return (
                          <div className="max-w-5xl m-auto">
                            <JarjestamislupaJSX
                              kohteet={kohteet}
                              lupa={lupa}
                              muut={props.muut}
                            />
                          </div>
                        );
                      }}
                    />
                  ) : null}
                </div>
              )}
            />
            <Route
              path={AppRoute.Paatokset}
              exact
              render={() => (
                <JulkisetTiedot
                  koulutusmuoto={koulutusmuoto}
                  jarjestaja={jarjestaja}
                  tulevatLuvat={tulevatLuvat}
                  voimassaOlevaLupa={voimassaOlevaLupa}
                />
              )}
            />
          </LocalizedSwitch>
        </div>
      </article>
    );
  },
  (cp, np) => {
    return equal(cp, np);
  }
);

Jarjestaja.propTypes = {
  history: PropTypes.object,
  JarjestamislupaJSX: PropTypes.func,
  kohteet: PropTypes.array,
  koulutusmuoto: PropTypes.object,
  location: PropTypes.object,
  lupa: PropTypes.object,
  match: PropTypes.object,
  organisation: PropTypes.object,
  path: PropTypes.string,
  url: PropTypes.string,
  user: PropTypes.object,
  tulevatLuvat: PropTypes.array,
  voimassaOlevaLupa: PropTypes.object,
  muut: PropTypes.object
};

Jarjestaja.displayName = "Järjestäjä";

export default Jarjestaja;
